import { ReactNode, useMemo } from 'react'

import { TypographyRebrand } from 'src/components/atoms/TypographyRebrand'

import { HeroCtaContainer } from './HeroCtaContainer'
import { HeroTitle, HeroTitleTypography } from './HeroTitleTypography'
import {
  HeroRebrandLayout,
  HeroRebrandLayoutProps
} from './layouts/HeroRebrandLayout'
import { HeroBackground } from './types'

type WidgetHeroRebrandProps = {
  title: HeroTitle
  eyebrow?: string
  intro?: string
  ctas?: ReactNode
  unified?: boolean
  widget?: React.ReactNode
  mode?: HeroRebrandLayoutProps['mode']
  darkHeader?: boolean
} & HeroBackground

const WidgetHeroRebrand = ({
  title,
  ctas,
  eyebrow,
  intro,
  unified,
  widget,
  layout,
  color,
  imageUrl,
  mode,
  pattern,
  darkHeader
}: WidgetHeroRebrandProps) => {
  const heroProps = useMemo<HeroRebrandLayoutProps>(() => {
    switch (layout) {
      case 'solid':
        return {
          layout: 'solid',
          color
        }
      case 'image':
        return {
          layout: 'image',
          imageUrl
        }
      default:
        return {
          layout,
          pattern
        }
    }
  }, [layout, color, imageUrl, pattern])

  return (
    <HeroRebrandLayout
      darkHeader={darkHeader}
      mode={mode}
      {...heroProps}
      unified={unified}
    >
      <div className='flex flex-col lg:flex-row pb-15 md:py-15 items-center gap-6 md:gap-3'>
        <div className='hero-text-content flex-1 flex flex-col w-full items-center lg:items-baseline [&>*:last-child]:mb-0'>
          {eyebrow ? (
            <TypographyRebrand variant='eyebrow'>{eyebrow}</TypographyRebrand>
          ) : null}
          <HeroTitleTypography title={title} variant='h1' />
          {intro ? (
            <TypographyRebrand variant='introduction'>
              {intro}
            </TypographyRebrand>
          ) : null}
          {ctas ? <HeroCtaContainer>{ctas}</HeroCtaContainer> : null}
        </div>
        {widget ? (
          <div className='flex flex-1 min-w-0 relative justify-end items-center'>
            {widget}
          </div>
        ) : null}
      </div>
    </HeroRebrandLayout>
  )
}

export default WidgetHeroRebrand
